import './App.css';
import './styles/globalFonts.css';
import 'bootstrap/dist/css/bootstrap.min.css';
// import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import React, {useState, useRef, useEffect} from 'react';
import { usePersistedAccordionStates } from './hooks/usePersistedSelections';
import HubsList from "./components/HubsList";
import ProjectsList from "./components/ProjectsList";
import SettingsFolderManager from "./components/SettingsFolderManager";
import CategoryTreeModal from "./components/CategoryTreeModal";
import DirectoryTreeView from "./components/ShowFolderContentTreeView";
import DirectoryButtonBar from "./components/DirectoryButtonBar";
import GetTopFolders from "./components/GetTopFolders";
import {GlobalProvider, useGlobalProvider} from './contexts/GlobalProvider';
import {AuthProvider} from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import LoginNew from './components/LoginNew';
import AssetTable from './components/AssetTable';
import AssetsButtonBar from './components/AssetsButtonBar';
import StatusMessage from './components/StatusMessage';
import ViewerLeft from './components/ViewerLeft';
import ViewerRight from './components/ViewerRight';
import AccordionItem from './components/Accordion';
import { useDocumentFetcher } from './hooks/useDocumentFetcher';
import { useAssets } from './hooks/useAssets';
import ResponsiveGroupedTable from './components/testing/ResponsiveGroupedTable';
import { AssetDataProvider } from './contexts/AssetDataContext';
import PersistentSelectionsDemo from './components/testing/PersistentSelectionsDemo';
// import TokenButton from './components/TokenButton';
// import TestTable from './components/TestTable';
// import AirTable from './components/AirTable';
// const queryClient = new QueryClient();

// Inner App component that uses the GlobalProvider context
function AppInner() {
    const { selectedAssetId, selectedProjectId, selectedCategoryId } = useGlobalProvider();
    const { getAccessToken, isAuthenticated } = useAuth(); // Get getAccessToken and isAuthenticated from AuthContext
    const [selectedLeftUrn, setSelectedLeftUrn] = useState('');
    const [selectedRightUrn, setSelectedRightUrn] = useState('');
    
    // States for AssetTable and AssetDataProvider
    const [assetFetchOptions, setAssetFetchOptions] = useState(`?includeCustomAttributes=true&filter[categoryId]=${selectedCategoryId}&limit=200`);
    const [reloadFlag, setReloadFlag] = useState(0); // Use a number to trigger reloads
    // const [isFullScreenViewerOpen, setIsFullScreenViewerOpen] = useState(false); // State for full screen viewer

    // Expose asset data props and getAccessToken globally for non-React parts of the application
    useEffect(() => {
        window.assetDataProps = {
            selectedProjectId,
            selectedCategoryId,
            assetFetchOptions,
            reloadFlag,
            setAssetFetchOptions,
            setReloadFlag
        };
        window.getAuthAccessToken = getAccessToken; // Expose getAccessToken globally
    }, [selectedProjectId, selectedCategoryId, assetFetchOptions, reloadFlag, getAccessToken]);

    // Use persistent accordion states hook
    const {
        isViewerAccordionOpen,
        isProjectsAccordionOpen,
        isExplorerAccordionOpen,
        isAssetsAccordionOpen,
        setViewerAccordionOpen,
        setProjectsAccordionOpen,
        setExplorerAccordionOpen,
        setAssetsAccordionOpen
    } = usePersistedAccordionStates();
    
    // Get category options for the button bar (only fetch metadata, not assets)
    // This useAssets call is now only for metadata, not for the main asset data
    const { categoryOptions } = useAssets(selectedProjectId, selectedCategoryId, '', null, null, getAccessToken);
    
    // Clear viewer URNs and close viewers when project ID changes
    useEffect(() => {
        console.log("Project ID changed in App, clearing viewer URNs and closing viewers");
        setSelectedLeftUrn('');
        setSelectedRightUrn('');
        
        // Close both viewers if they are open
        if (viewerLeftRef.current && viewerLeftRef.current.closeViewer) {
            console.log("Closing left viewer due to project change");
            viewerLeftRef.current.closeViewer();
        }
        
        if (viewerRightRef.current && viewerRightRef.current.closeViewer) {
            console.log("Closing right viewer due to project change");
            viewerRightRef.current.closeViewer();
        }
    }, [selectedProjectId]);
    
    // Automatically fetch assets when category changes
    useEffect(() => {
        if (selectedCategoryId && selectedProjectId) {
            console.log("Category ID changed in App, updating fetch options and triggering reload");
            setAssetFetchOptions(`?includeCustomAttributes=true&filter[categoryId]=${selectedCategoryId}&limit=200`);
            setReloadFlag(prev => prev + 1);
        }
    }, [selectedCategoryId, selectedProjectId]);
    
    // Use the custom hook for document fetching
    const { documentData, loading: documentsLoading, error: documentsError, fetchDocuments, clearDocuments } = useDocumentFetcher();

    // References to viewer components to access their openViewer functions
    const viewerLeftRef = useRef(null);
    const viewerRightRef = useRef(null);
    
    // References to asset components to access their functions
    const assetTableRef = useRef(null);
    
    // State for CategoryTree modal
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    
    // State for Link Viewers toggle (default off)
    const [linkViewersEnabled, setLinkViewersEnabled] = useState(false);


    const handleSetSelectedLeftUrn = (urn) => {
        console.log('Selected handleSetSelectedLeftUrn:', urn);
        const encodedUrn = btoa(urn);
        setSelectedLeftUrn(encodedUrn);
    };

    const handleSetSelectedRightUrn = (urn) => {
        console.log('Selected handleSetSelectedRightUrn:', urn);
        const encodedUrn = btoa(urn);
        setSelectedRightUrn(encodedUrn);
    };

    // Functions to open viewers - these will be called by DirectoryButtonBar
    const handleOpenViewerLeft = () => {
        if (viewerLeftRef.current && viewerLeftRef.current.openViewer) {
            // Ensure the accordion containing the viewers is visible before
            // initializing the viewer. Otherwise the canvas may have zero size.
            setViewerAccordionOpen(true);
            viewerLeftRef.current.openViewer();
        }
    };

    const handleOpenViewerRight = () => {
        if (viewerRightRef.current && viewerRightRef.current.openViewer) {
            setViewerAccordionOpen(true);
            viewerRightRef.current.openViewer();
        }
    };

    // Handlers for AssetTable to update its fetch options and trigger reloads
    const handleAssetSearchChange = (options) => {
        setAssetFetchOptions(options);
    };

    const handleAssetReload = () => {
        setReloadFlag(prev => prev + 1);
    };

    // Handler functions for the AssetsButtonBar
    const handleGetDocuments = () => {
        fetchDocuments(selectedProjectId, selectedAssetId);
    };
    
    const handleGetAssets = () => {
        // This will now trigger the reload via the handleAssetReload prop passed to AssetTable
        if (assetTableRef.current) {
            assetTableRef.current.handleReloadClick();
        }
        clearDocuments();
    };
    
    const handleOpenCategoryModal = () => {
        setIsCategoryModalOpen(true);
    };
    
    const handleCloseCategoryModal = () => {
        setIsCategoryModalOpen(false);
    };
    
    const handleToggleLinkViewers = () => {
        setLinkViewersEnabled(prev => !prev);
    };

    // const handleToggleFullScreenViewer = () => {
    //     setIsFullScreenViewerOpen(prev => !prev);
    // };


    return (
        // <QueryClientProvider client={queryClient}>
        <AssetDataProvider
            selectedProjectId={selectedProjectId}
            selectedCategoryId={selectedCategoryId}
            assetFetchOptions={assetFetchOptions}
            reloadFlag={reloadFlag}
            onProgress={(info) => console.log('Asset fetch progress:', info)}
            getAccessToken={getAccessToken}
        >
            <div className="App  container-fluid">
                {/*<AirTable column={column} value={value} />*/}
                <AccordionItem title="Login and Projects " isOpen={isProjectsAccordionOpen} setIsOpen={setProjectsAccordionOpen}>

                    <div className="row">
                        <div className="col-md-6">
                            <div>
                                <LoginNew/>
                            </div>

                            <div className="hubsList">
                                <HubsList/>
                            </div>
                            <div className="projectsList">
                                <ProjectsList/>
                            </div>


                        </div>
                        <div className="col-md-6">
                            {/*<SettingsFolderManager/>*/}
                            <GetTopFolders/>
                        </div>
                    </div>
                    
                    {/* Temporary demo component for testing persistent selections */}
                    {/*<div className="row">*/}
                    {/*    <div className="col-12">*/}
                    {/*        <PersistentSelectionsDemo />*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                </AccordionItem>
            <AccordionItem title="File explorer" isOpen={isExplorerAccordionOpen} setIsOpen={setExplorerAccordionOpen} disabled={!isAuthenticated}>
                <div className="row">
                    <div className="col-md-12">
                        <DirectoryButtonBar
                            setSelectedLeftUrn={handleSetSelectedLeftUrn}
                            setSelectedRightUrn={handleSetSelectedRightUrn}
                            openViewerLeft={handleOpenViewerLeft}
                            openViewerRight={handleOpenViewerRight}
                        />
                        <DirectoryTreeView/>
                    </div>
                </div>
            </AccordionItem>
                <AccordionItem title="Viewers" isOpen={isViewerAccordionOpen} setIsOpen={setViewerAccordionOpen} disabled={!isAuthenticated}>
                    <div className="row fullRow">
                        <div className="col-md-6">
                            <div className="viewerLeft viewerContainer">
                                <ViewerLeft
                                    ref={viewerLeftRef}
                                    urn={selectedLeftUrn}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="viewerRight viewerContainer">
                                <ViewerRight
                                    ref={viewerRightRef}
                                    urn={selectedRightUrn}
                                />
                            </div>
                        </div>
                    </div>

                </AccordionItem>
                <AccordionItem title="Assets" isOpen={isAssetsAccordionOpen} setIsOpen={setAssetsAccordionOpen} disabled={!isAuthenticated}>
                    <div className="row fullRow">
                        <div className="assetRelationShipsContainer">
                            <StatusMessage
                                type="warning"
                                message={documentsError}
                            />
                        </div>
                    </div>
                    <div className="row fullRow">
                        <div className="assetContainer">
                            <AssetsButtonBar
                                onGetAssets={handleGetAssets}
                                onGetDocuments={handleGetDocuments}
                                onOpenCategories={handleOpenCategoryModal}
                                documentData={documentData}
                                setSelectedLeftUrn={handleSetSelectedLeftUrn}
                                setSelectedRightUrn={handleSetSelectedRightUrn}
                                openViewerLeft={handleOpenViewerLeft}
                                openViewerRight={handleOpenViewerRight}
                                categoryOptions={categoryOptions}
                                linkViewersEnabled={linkViewersEnabled}
                                onToggleLinkViewers={handleToggleLinkViewers}
                            />
                            {/*<button onClick={handleToggleFullScreenViewer} className="btn btn-info mb-3">*/}
                            {/*    Toggle Full Screen Viewer*/}
                            {/*</button>*/}
                            <AssetTable
                                ref={assetTableRef}
                                linkViewersEnabled={linkViewersEnabled}
                                onSearchChange={handleAssetSearchChange}
                                onReload={handleAssetReload}
                            />
                        </div>
                    </div>

                </AccordionItem>
                {/*<ResponsiveGroupedTable/>*/}
                <CategoryTreeModal
                    isOpen={isCategoryModalOpen}
                    onClose={handleCloseCategoryModal}
                    onCategorySet={handleGetAssets}
                />

            </div>
        </AssetDataProvider>
        // </QueryClientProvider>
    );
}

// Main App component that provides the context
function App() {
    return (
        <AuthProvider>
            <GlobalProvider>
                <AppInner />
            </GlobalProvider>
        </AuthProvider>
    );
}

export default App;
